<script setup lang="ts">
import { computed } from 'vue';
import AppLogoIcon from '@/components/AppLogoIcon.vue';
import { useSite } from '@/composables/useSite';

const { siteDisplayName, isPdfSite, isImageSite } = useSite();

const props = withDefaults(
    defineProps<{
        /** sm: mobile drawer; md: header & sidebar (default); lg: auth layouts */
        size?: 'sm' | 'md' | 'lg';
    }>(),
    { size: 'md' },
);

const boxClass = computed(
    () =>
        ({
            sm: 'size-6 rounded',
            md: 'size-8 rounded-md',
            lg: 'size-9 rounded-md',
        })[props.size],
);

const glyphClass = computed(
    () =>
        ({
            sm: 'size-4',
            md: 'size-5',
            lg: 'size-6',
        })[props.size],
);

const brandTileClass =
    'border-0 bg-transparent shadow-none ring-0 dark:border-transparent dark:bg-transparent dark:shadow-none';

const neutralTileClass =
    'border border-border/80 bg-muted text-foreground dark:border-border dark:bg-muted/80';

/** Tile behind the mark: PDF/image use flat container for raster logo; else neutral. */
const tileSurfaceClass = computed(() => {
    if (isPdfSite.value || isImageSite.value) {
        return brandTileClass;
    }

    return neutralTileClass;
});

const brandLogoSrc = computed(() =>
    isPdfSite.value ? '/images/pdf/logo.webp' : '/images/image/logo.webp',
);
</script>

<template>
    <div class="flex min-w-0 items-center">
        <div
            :class="[
                'flex shrink-0 items-center justify-center overflow-hidden',
                boxClass,
                tileSurfaceClass,
            ]"
        >
            <img
                v-if="isPdfSite || isImageSite"
                :src="brandLogoSrc"
                alt=""
                class="h-full w-full shrink-0 object-contain"
                loading="eager"
                decoding="async"
                aria-hidden="true"
            />
            <AppLogoIcon
                v-else
                :class="[
                    'fill-current text-foreground dark:text-foreground',
                    glyphClass,
                ]"
            />
        </div>
        <div class="ml-1 grid min-w-0 flex-1 text-left text-3xl leading-tight">
            <span
                v-if="isPdfSite"
                class="inline-flex min-w-0 items-baseline gap-[2px] font-bold tracking-tight select-none"
            >
                <span class="text-neutral-950 dark:text-neutral-50">pdf</span>
                <span class="text-blue-600 dark:text-blue-400">easy</span>
                <sup
                    class="[vertical-align:0.35em] align-baseline text-[0.42em] leading-none font-semibold text-neutral-600 dark:text-neutral-400"
                    >.app</sup
                >
            </span>
            <span
                v-else-if="isImageSite"
                class="inline-flex min-w-0 items-baseline gap-[2px] font-bold tracking-tight select-none"
            >
                <span class="text-neutral-950 dark:text-neutral-50"
                    >optimize</span
                >
                <span class="text-emerald-600 dark:text-emerald-400"
                    >image</span
                >
                <sup
                    class="[vertical-align:0.35em] align-baseline text-[0.42em] leading-none font-semibold text-neutral-600 dark:text-neutral-400"
                    >.net</sup
                >
            </span>
            <span
                v-else
                class="truncate leading-tight font-semibold text-foreground"
                >{{ siteDisplayName }}</span
            >
        </div>
    </div>
</template>
